import { computed, onScopeDispose, ref, watch, type Ref } from 'vue'
import { usePlayerStore, type PlayerTrack } from '~/stores/player'
import { useCreator } from '../creator/useCreator'

interface LikeState { id: string; liked: boolean; like_count: number }
interface SongLikeOptions {
  surface?: 'player' | 'share_page'
  readState?: (id: string) => Promise<LikeState>
}

/** Shared presentation for the existing authenticated like/unlike contract. */
export function useSongLike(song: Ref<PlayerTrack | null | undefined>, options: SongLikeOptions = {}) {
  const player = usePlayerStore()
  const { isAuthenticated, handleSignIn } = useAuthState()
  const { request, identity } = useCreator()
  const busy = ref(false)
  const failed = ref(false)
  const initialStateValid = ref(true)
  let active = true
  onScopeDispose(() => { active = false })
  watch(identity, () => {
    if (!options.readState) return
    initialStateValid.value = false
    failed.value = false
    if (song.value) delete player.likeUpdates[song.value.id]
  }, { flush: 'sync' })
  const liked = computed(() => {
    if (!song.value || options.readState && !isAuthenticated.value) return false
    return player.likeUpdates[song.value.id]?.liked ?? ((!options.readState || initialStateValid.value) && song.value.liked_by_me === true)
  })
  const toggle = async () => {
    const track = song.value
    if (!track?.id || busy.value) return
    if (!isAuthenticated.value) { await handleSignIn(); return }
    const owner = identity.value
    const isCurrent = () => active && owner === identity.value
    busy.value = true
    failed.value = false
    try {
      let wasLiked = liked.value
      // Shared SSR payloads can omit viewer state. Read it only after interaction.
      if (options.readState) {
        const state = await options.readState(track.id)
        if (!isCurrent()) return
        if (state?.id !== track.id || typeof state.liked !== 'boolean' || !Number.isFinite(state.like_count)) throw new Error('Invalid like state')
        wasLiked = state.liked
        player.likeUpdates[track.id] = { liked: state.liked, like_count: Math.max(0, state.like_count) }
      }
      const data = await request<LikeState>(wasLiked ? 'unlike' : 'like', { id: track.id, surface: options.surface || 'player' })
      if (!isCurrent()) return
      if (!data || (data.id !== undefined || options.readState) && data.id !== track.id || typeof data.liked !== 'boolean' || !Number.isFinite(data.like_count)) throw new Error('Invalid like response')
      const updates = { liked_by_me: data.liked, like_count: Math.max(0, data.like_count) }
      player.likeUpdates[track.id] = { liked: data.liked, like_count: updates.like_count }
      for (const item of [track, ...player.queue, player.currentTrack]) if (item?.id === track.id) Object.assign(item, updates)
    } catch { if (isCurrent() && song.value?.id === track.id) failed.value = true }
    finally { busy.value = false }
  }
  return { liked, busy, failed, toggle }
}
